import { aggravates } from '../constants/aggravates';
import { BodyParts } from '../types/Survey.type';

/**
 * Returns the deduplicated list of aggravates options applicable to the
 * given body-part selection.  This is the single source of truth used by
 * both the standard CCS question flow and by custom CCS surveys that
 * include a provocative question.
 */
export const getAggravatesForBodyParts = (parts: BodyParts): { en: string; es: string }[] => {
  let options: { en: string; es: string }[] = aggravates.all;

  if (
    parts.front.head?.left ||
    parts.front.head?.right ||
    parts.rear.head?.left ||
    parts.rear.head?.right
  ) {
    options = options.concat(aggravates.head);
  }

  if (
    parts.front.foot?.left ||
    parts.front.foot?.right ||
    parts.rear.foot?.left ||
    parts.rear.foot?.right ||
    parts.front.ankle?.left ||
    parts.front.ankle?.right ||
    parts.rear.ankle?.left ||
    parts.rear.ankle?.right
  ) {
    options = options.concat(aggravates.footAnkle);
  }

  if (
    parts.front.knee?.left ||
    parts.front.knee?.right ||
    parts.rear.knee?.left ||
    parts.rear.knee?.right ||
    parts.front.leg?.left ||
    parts.front.leg?.right ||
    parts.rear.leg?.left ||
    parts.rear.leg?.right ||
    parts.front.thigh?.left ||
    parts.front.thigh?.right ||
    parts.rear.thigh?.left ||
    parts.rear.thigh?.right
  ) {
    options = options.concat(aggravates.kneeLeg);
  }

  if (
    parts.front.hip?.left ||
    parts.front.hip?.right ||
    parts.rear.hip?.left ||
    parts.rear.hip?.right
  ) {
    options = options.concat(aggravates.hip);
  }

  const hasLowerBackIssue =
    parts.rear['lower-back']?.left ||
    parts.rear['lower-back']?.right ||
    parts.rear['upper-lower-back']?.left ||
    parts.rear['upper-lower-back']?.right;
  const hasUpperBackIssue = parts.rear['upper-back']?.left || parts.rear['upper-back']?.right;
  const hasRearNeckIssue = parts.rear.neck?.left || parts.rear.neck?.right;

  const hasNeckIssue = hasRearNeckIssue || parts.front.neck?.left || parts.front.neck?.right;

  const isFullSpine = !!(hasLowerBackIssue && hasUpperBackIssue && hasRearNeckIssue);

  if (isFullSpine) {
    options = options.concat(aggravates.fullSpine);
  } else if (hasLowerBackIssue || hasUpperBackIssue) {
    options = options.concat(aggravates.back);

    if (
      (parts.front.rib?.left || parts.front.rib?.right) &&
      (parts.rear['upper-back']?.left || parts.rear['upper-back']?.right)
    ) {
      options = options.concat(aggravates.backRib);
    }
  }

  // Neck options are added independently of back so compound complaints
  // like "Neck and Upper Back" include both sets. Skipped only when
  // fullSpine already matched (it covers neck-relevant items).
  if (hasNeckIssue && !isFullSpine) {
    options = options.concat(aggravates.neck);
  }

  if (
    parts.front.elbow?.left ||
    parts.front.elbow?.right ||
    parts.rear.elbow?.left ||
    parts.rear.elbow?.right ||
    parts.front.forearm?.left ||
    parts.front.forearm?.right ||
    parts.rear.forearm?.left ||
    parts.rear.forearm?.right
  ) {
    options = options.concat(aggravates.elbowForearm);
  }

  if (
    parts.front.shoulder?.left ||
    parts.front.shoulder?.right ||
    parts.rear.shoulder?.left ||
    parts.rear.shoulder?.right ||
    parts.front.arm?.left ||
    parts.front.arm?.right ||
    parts.rear.arm?.left ||
    parts.rear.arm?.right
  ) {
    options = options.concat(aggravates.arm);
  }

  if (
    parts.front.hand?.left ||
    parts.front.hand?.right ||
    parts.rear.hand?.left ||
    parts.rear.hand?.right ||
    parts.front.wrist?.left ||
    parts.front.wrist?.right ||
    parts.rear.wrist?.left ||
    parts.rear.wrist?.right
  ) {
    options = options.concat(aggravates.hand);
  }

  // Deduplicate by English key
  const seen: Record<string, boolean> = {};

  return options.filter(item => {
    if (seen[item.en]) return false;
    seen[item.en] = true;

    return true;
  });
};
